/**
 * Agent encryption settings management for localStorage
 * Settings are stored per user+agent combination and persist forever
 */

export interface AgentEncryptionSettings {
  enabled: boolean
  key: string
}

export interface AgentOpenAISettings {
  enabled: boolean
  apiKey: string
}

export interface AgentAnthropicSettings {
  enabled: boolean
  apiKey: string
}

const STORAGE_KEY_PREFIX = 'mazpin_agent_encryption_'
const OPENAI_STORAGE_KEY_PREFIX = 'mazpin_agent_openai_'
const ANTHROPIC_STORAGE_KEY_PREFIX = 'mazpin_agent_anthropic_'
const API_KEY_STORAGE_PREFIX = 'mazpin_agent_apikey_'

/**
 * Gets the current user ID from localStorage or auth store
 * @returns The user ID or 'anonymous' if not logged in
 */
function getCurrentUserId(): string {
  try {
    // Try to get user from localStorage first
    const userStr = localStorage.getItem('user')
    if (userStr) {
      const user: unknown = JSON.parse(userStr)
      if (user && typeof user === 'object' && 'id' in user) {
        const typedUser = user as { id: unknown }
        if (typeof typedUser.id === 'string') {
          return typedUser.id
        }
      }
    }
    
    // Fallback to token-based identification or anonymous
    const token = localStorage.getItem('token')
    if (token) {
      // Use a hash of the token as user identifier if no user ID available
      return btoa(token).slice(0, 16)
    }
    
    return 'anonymous'
  } catch (error) {
    console.warn('Failed to get user ID for encryption settings:', error)
    return 'anonymous'
  }
}

/**
 * Generates storage key for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${STORAGE_KEY_PREFIX}${userId}_${agentId}`
}

/**
 * Gets encryption settings for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The encryption settings or default values
 */
export function getAgentEncryptionSettings(agentId: string): AgentEncryptionSettings {
  const storageKey = getStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed !== null) {
        const candidate = parsed as Partial<AgentEncryptionSettings>
        return {
          enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
          key: typeof candidate.key === 'string' ? candidate.key : ''
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse agent encryption settings:', error)
  }

  // Auto-fill from env var for local development if localStorage has no key
  const envEncryptionKey = String(import.meta.env.VITE_TEST_ENCRYPTION_KEY || '')
  if (envEncryptionKey) {
    const settings: AgentEncryptionSettings = { enabled: true, key: envEncryptionKey }
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings))
    } catch {
      // Ignore localStorage write failures
    }
    return settings
  }

  // Return default settings
  return {
    enabled: false,
    key: ''
  }
}

/**
 * Saves encryption settings for a specific agent for the current user
 * @param agentId The agent ID
 * @param settings The encryption settings to save
 */
export function saveAgentEncryptionSettings(agentId: string, settings: AgentEncryptionSettings): void {
  const storageKey = getStorageKey(agentId)
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save agent encryption settings:', error)
    throw new Error('Failed to save encryption settings', { cause: error })
  }
}

/**
 * Generates storage key for OpenAI settings for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getOpenAIStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${OPENAI_STORAGE_KEY_PREFIX}${userId}_${agentId}`
}

/**
 * Generates storage key for Anthropic settings for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getAnthropicStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${ANTHROPIC_STORAGE_KEY_PREFIX}${userId}_${agentId}`
}

/**
 * Gets OpenAI API key settings for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The OpenAI settings or default values
 */
export function getAgentOpenAISettings(agentId: string): AgentOpenAISettings {
  const storageKey = getOpenAIStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed !== null) {
        const candidate = parsed as Partial<AgentOpenAISettings>
        return {
          enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
          apiKey: typeof candidate.apiKey === 'string' ? candidate.apiKey : ''
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse agent OpenAI settings:', error)
  }

  // Auto-fill from env var for local development if localStorage has no key
  const envOpenAIKey = String(import.meta.env.VITE_TEST_OPENAI_API_KEY || '')
  if (envOpenAIKey) {
    const settings: AgentOpenAISettings = { enabled: true, apiKey: envOpenAIKey }
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings))
    } catch {
      // Ignore localStorage write failures
    }
    return settings
  }

  // Return default settings
  return {
    enabled: false,
    apiKey: ''
  }
}

/**
 * Saves OpenAI API key settings for a specific agent for the current user
 * @param agentId The agent ID
 * @param settings The OpenAI settings to save
 */
export function saveAgentOpenAISettings(agentId: string, settings: AgentOpenAISettings): void {
  const storageKey = getOpenAIStorageKey(agentId)
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save agent OpenAI settings:', error)
    throw new Error('Failed to save OpenAI settings', { cause: error })
  }
}

/**
 * Gets Anthropic API key settings for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The Anthropic settings or default values
 */
export function getAgentAnthropicSettings(agentId: string): AgentAnthropicSettings {
  const storageKey = getAnthropicStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed: unknown = JSON.parse(stored)
      if (typeof parsed === 'object' && parsed !== null) {
        const candidate = parsed as Partial<AgentAnthropicSettings>
        return {
          enabled: typeof candidate.enabled === 'boolean' ? candidate.enabled : false,
          apiKey: typeof candidate.apiKey === 'string' ? candidate.apiKey : ''
        }
      }
    }
  } catch (error) {
    console.warn('Failed to parse agent Anthropic settings:', error)
  }

  // Auto-fill from env var for local development if localStorage has no key
  const envAnthropicKey = String(import.meta.env.VITE_TEST_ANTHROPIC_API_KEY || '')
  if (envAnthropicKey) {
    const settings: AgentAnthropicSettings = { enabled: true, apiKey: envAnthropicKey }
    try {
      localStorage.setItem(storageKey, JSON.stringify(settings))
    } catch {
      // Ignore localStorage write failures
    }
    return settings
  }

  // Return default settings
  return {
    enabled: false,
    apiKey: ''
  }
}

/**
 * Saves Anthropic API key settings for a specific agent for the current user
 * @param agentId The agent ID
 * @param settings The Anthropic settings to save
 */
export function saveAgentAnthropicSettings(agentId: string, settings: AgentAnthropicSettings): void {
  const storageKey = getAnthropicStorageKey(agentId)
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings))
  } catch (error) {
    console.error('Failed to save agent Anthropic settings:', error)
    throw new Error('Failed to save Anthropic settings', { cause: error })
  }
}

/**
 * Generates storage key for agent API key for user+agent combination
 * @param agentId The agent ID
 * @returns The storage key
 */
function getApiKeyStorageKey(agentId: string): string {
  const userId = getCurrentUserId()
  return `${API_KEY_STORAGE_PREFIX}${userId}_${agentId}`
}

/**
 * Gets agent API key for a specific agent for the current user
 * @param agentId The agent ID
 * @returns The API key or empty string if not found
 */
export function getAgentApiKey(agentId: string): string {
  const storageKey = getApiKeyStorageKey(agentId)
  try {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      return stored
    }
  } catch (error) {
    console.warn('Failed to get agent API key:', error)
  }
  
  return ''
}

/**
 * Saves agent API key for a specific agent for the current user
 * @param agentId The agent ID
 * @param apiKey The API key to save
 */
export function saveAgentApiKey(agentId: string, apiKey: string): void {
  const storageKey = getApiKeyStorageKey(agentId)
  try {
    if (apiKey) {
      localStorage.setItem(storageKey, apiKey)
    } else {
      // Remove the key if empty
      localStorage.removeItem(storageKey)
    }
  } catch (error) {
    console.error('Failed to save agent API key:', error)
    throw new Error('Failed to save API key', { cause: error })
  }
}

